/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { register } from '../registry.js';

/*
 * The alert.
 *
 * `showModal()` and little else, for the reasons the sheet gives at more length: the dialog
 * element already does the backdrop, the focus trap, Escape, the top layer, `inert` on the page
 * behind it and the return of focus to whatever opened it.
 *
 * What is here instead is the two things a server-rendered alert needs and a sheet does not. One
 * is raising it exactly once, because the app raises an alert at the moment the work finishes and
 * a page can only arrive with it already on. The other is taking the query parameter that caused
 * it out of the address bar afterwards, because a page is a thing you can reload and an alert is
 * not a thing you want to be told twice.
 *
 * The third thing is the one the sheet does too: the panel takes the focus as it opens, rather
 * than letting `showModal()` put it on the one button in the alert.
 *
 * Like the sheet, an alert needs this file to appear at all -- a dialog that has not been shown is
 * `display: none`, and nothing in CSS can show it. That is the one place this library's "nothing
 * here is required" rule does not reach, and the same trade the sheet already makes.
 */

/**
 * Marks an alert that has been raised, so that painting again does not raise it again.
 *
 * `paint` is idempotent by contract, and for an alert idempotent has to mean this. A Turbo frame
 * rendering, a `wp:paint` from a controller, a restored page coming back out of Turbo's cache:
 * each of those repaints markup that is already on the screen, and `if (!alert.open)` -- which is
 * the test the sheet can afford -- is true of every alert the reader has just dismissed. Without
 * the marker, acknowledging an alert was an invitation for it to come back.
 */
const SHOWN = 'data-wp-alert-shown';

/**
 * Stops an alert handing the focus to the button that acknowledges it.
 *
 * `showModal()` focuses the first focusable descendant, and in an alert that is the preferred
 * action -- usually the only control in the panel. So every alert opened with a ring drawn round
 * its one button, which reads as the panel pressing the reader rather than waiting to be read. The
 * panel takes the focus instead: a reader still lands inside the alert, which is what the focus
 * trap and Escape need, with nothing in it singled out, and a Tab from there reaches the action.
 *
 * `tabindex`, because a dialog is not focusable without one. By hand rather than with `autofocus`,
 * which is only honoured on a descendant. And here, unlike in the sheet, there is nothing to watch
 * for: both of the ways an alert opens go through this file, so the focus can move on the line
 * after the one that opened it.
 */
export function focusWholeAlert(alert: HTMLDialogElement): void {
    alert.setAttribute('tabindex', '-1');
    alert.focus({ preventScroll: true });
}

/** An alert the page arrived carrying: the confirmation of something that has already happened. */
export function openOnLoad(alert: HTMLDialogElement): void {
    if (alert.hasAttribute(SHOWN)) {
        return;
    }

    /* Before `showModal()` and not after: the attribute is what makes this call the only one, and
       a repaint triggered from inside the opening -- `paintAll` on a sheet, say -- would otherwise
       find an alert that is open but not yet marked. */
    alert.setAttribute(SHOWN, '');
    alert.showModal();
    focusWholeAlert(alert);
}

/**
 * Takes the parameter that raised this alert out of the address bar.
 *
 * The page answered at `?success=true` and rendered the alert because of it, so the alert is in
 * the URL rather than in any state the reader can clear: reload, or come back to the page with the
 * back button, and the server renders it again. Inline that was untidy; as a modal it is a panel
 * standing between the reader and the screen they asked for, saying something they read a minute
 * ago. iOS has no equivalent of the problem because iOS has no address bar.
 *
 * `replaceState` and not `pushState`: this is not a navigation and must not become a thing to go
 * back to. The existing state object is carried over for Turbo, which keeps its own restoration
 * data in there and loses the scroll position if this hands it a null.
 */
export function forgetInTheUrl(alert: HTMLDialogElement): void {
    const name = alert.getAttribute('data-wp-alert-dismiss-param');
    if (name === null || name === '') {
        return;
    }

    const url = new URL(window.location.href);
    if (!url.searchParams.has(name)) {
        return;
    }

    url.searchParams.delete(name);
    window.history.replaceState(window.history.state, '', url);
}

export function paint(root: ParentNode): void {
    root.querySelectorAll<HTMLDialogElement>('.wp-alert[data-wp-alert-open-on-load]').forEach(openOnLoad);
}

export function listen(): void {
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        /* An alert raised by something on the page rather than by the page arriving. Nothing in
           the app does this yet -- a confirmation is always the answer to a round trip -- but the
           gallery has to be able to open one to show it, and a component that can only be seen in
           production is one nobody reviews. */
        const opener = target.closest('[data-wp-alert-open]');
        if (opener !== null) {
            event.preventDefault();

            const named = document.getElementById(opener.getAttribute('data-wp-alert-open') ?? '');
            if (named instanceof HTMLDialogElement) {
                named.showModal();
                focusWholeAlert(named);
            }

            return;
        }

        const closer = target.closest('[data-wp-alert-close]');
        if (closer !== null) {
            event.preventDefault();
            closer.closest('dialog')?.close();
        }

        /* And no backdrop case, which is where this parts company with the sheet. A click on the
           dim area has the dialog itself as its target and the sheet reads that as "outside the
           panel, so close"; an alert is a sentence to be acknowledged, and UIKit's does not let
           you dismiss it by missing. */
    });

    /*
     * In the capture phase, because `close` does not bubble. A non-bubbling event still travels
     * down from the document to its target, so capturing is what lets one listener serve every
     * alert on the page -- including the ones Turbo has not rendered yet, which is the property
     * the whole library is built on.
     *
     * On `close` rather than on the button, so that Escape counts too: the parameter has to go
     * whichever way the alert was acknowledged, or reloading brings back an alert the reader
     * dismissed with the keyboard.
     */
    document.addEventListener(
        'close',
        (event) => {
            const target = event.target;
            if (target instanceof HTMLDialogElement && target.matches('.wp-alert')) {
                forgetInTheUrl(target);
            }
        },
        true
    );

    register(paint);
}
